import { Alert } from "@circleco/compass/components/Alert";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import { ReportHeader } from "@/react/components/SettingsApp/Moderation/ReportFlaggedContentModal/CompassModal/ReportHeader";
import { ContactSnapshotAlert } from "@/react/components/SettingsApp/Moderation/ReportFlaggedContentModal/ContactSnapshotAlert";
import { FlaggedChatMessageBody } from "@/react/components/SettingsApp/Moderation/ReportFlaggedContentModal/FlaggedChatMessageBody";
import { ReportFlaggedContentable } from "@/react/components/SettingsApp/Moderation/ReportFlaggedContentModal/ReportFlaggedContentable";
import {
  getContentProps,
  shouldShowContactSnapshot,
} from "@/react/components/SettingsApp/Moderation/utils";
import {
  CHAT_ROOM_MESSAGE_TYPE,
  POST_TYPE,
} from "@/react/types/FlaggedContent";
import type { FlaggedContent } from "@/react/types/FlaggedContent";

const i18nRoot = "settings.moderation.review_flagged_content_modal";

interface ModerationDetailContentProps {
  authorName?: string;
  flaggedContent: FlaggedContent;
  onNavigate: () => void;
}

export const ModerationDetailContent = ({
  authorName: providedAuthorName,
  flaggedContent,
  onNavigate,
}: ModerationDetailContentProps) => {
  const {
    action_by_community_member_name: actionByCommunityMemberName,
    action_reason_body: actionReasonBody,
    author,
    contact_snapshot: contactSnapshot,
    content_modified_since_report: isContentModified,
    content_snapshot: contentSnapshot,
    flagged_contentable,
  } = flaggedContent;

  const {
    initiator,
    is_image_post: isImagePost,
    post_slug: postSlug,
    slug,
    space,
    title,
    type,
  } = flagged_contentable ?? {};

  if (!type) return null;

  const shouldShowNote =
    Boolean(actionByCommunityMemberName) && Boolean(actionReasonBody?.trim());
  const shouldShowTitle = title && !isImagePost && type === POST_TYPE;
  const authorName = providedAuthorName ?? author?.name ?? initiator?.name;
  const isChatMessageType = type === CHAT_ROOM_MESSAGE_TYPE;

  return (
    <div className="flex min-h-0 flex-col gap-6">
      {shouldShowContactSnapshot(contactSnapshot, authorName) && (
        <ContactSnapshotAlert
          authorName={authorName ?? ""}
          contactSnapshot={contactSnapshot}
        />
      )}
      {shouldShowNote && (
        <Alert
          icon="circle-info"
          iconColor="warning"
          variant="warning"
          title={t([i18nRoot, "moderation_note_title"], {
            user: actionByCommunityMemberName,
          })}
          description={actionReasonBody}
        />
      )}
      {isContentModified && (
        <Alert
          description={t([i18nRoot, "content_modified_alert"])}
          icon="circle-info"
          variant="primary"
        />
      )}
      <div className="border-primary overflow-hidden rounded-2xl border">
        <div className="flex flex-col gap-4 p-5">
          <ReportHeader
            flaggedContent={flaggedContent}
            onNavigate={onNavigate}
          />
          {shouldShowTitle && (
            <Typography variant="heading-md" color="primary">
              {title}
            </Typography>
          )}
          {isChatMessageType ? (
            <div
              className="min-h-0 min-w-0 flex-1 overflow-y-auto"
              data-flagged-chat-scroll-container
            >
              <FlaggedChatMessageBody flaggedContent={flaggedContent} />
            </div>
          ) : (
            <ReportFlaggedContentable
              {...getContentProps(
                type,
                contentSnapshot?.note,
                flagged_contentable,
              )}
              isImagePost={isImagePost}
              postSlug={postSlug || slug}
              spaceId={space?.id}
            />
          )}
        </div>
      </div>
    </div>
  );
};
